/**
 * Week Ahead: calendar days and row caps for each day.
 *
 * Today's default view becomes the week ahead (8.2). This answers which days
 * to show and how many rows each can display. No imports.
 */

export interface DayPlan {
  day: string
  from: string
  cap: number
  weekend: boolean
}

/** Weekday evening is at this hour — Doug: "I'm not going to be around during
 *  the day because I'm working". Restated from today.ts's own constant. */
export const EVENING_HOUR = 17

/** How many rows a weeknight can show. */
export const WEEKNIGHT_CAP = 3

/** How many rows a Saturday or Sunday normally shows. */
export const WEEKEND_DAY_CAP = 6

/** How many rows weekend days show when looked at from Thursday or Friday —
 *  Doug: "if it's a Thursday or a Friday then I do want more of a full
 *  weekend program". */
export const WEEKEND_AHEAD_CAP = 8

// `zone.ts`'s `offsetMinIn`, `instantIn` and `dayIn`, restated because this
// file has no imports (C2.1: the zone is a parameter, as `home` is in
// `reach.ts`). Called seven or eight times a render, so no formatter cache.

/** `tz`'s offset from UTC in minutes: Berlin 120 in summer, 60 in winter. */
function offsetMin(at: Date, tz: string): number {
  const local = at.toLocaleString('sv-SE', { timeZone: tz })
  return (Date.parse(`${local.replace(' ', 'T')}Z`) - at.getTime()) / 60000
}

/** The instant at which a wall clock in `tz` reads `hour:00` on `day`. */
function instantAt(day: string, hour: number, tz: string): Date {
  const naive = Date.parse(`${day}T${String(hour).padStart(2, '0')}:00:00Z`)
  const first = new Date(naive - offsetMin(new Date(naive), tz) * 60000)
  return new Date(naive - offsetMin(first, tz) * 60000)
}

/** The calendar day a moment falls on in `tz`. */
function dayIn(at: Date | string, tz: string): string {
  return new Date(at).toLocaleDateString('en-CA', { timeZone: tz })
}

/** Day of week (0=Sunday, 1=Monday, ..., 6=Saturday). */
function dayOfWeek(isoDay: string): number {
  return new Date(`${isoDay}T12:00:00Z`).getUTCDay()
}

/** Add n days to an ISO date string. */
function addDays(s: string, n: number): string {
  const d = new Date(`${s}T12:00:00Z`)
  return new Date(d.getTime() + n * 86400000).toISOString().slice(0, 10)
}

/**
 * The seven calendar days starting tomorrow, extended through Sunday.
 *
 * Starts tomorrow (today is Tonight's, which 8.2 keeps separate). Returns
 * eight days only when today is Saturday, giving Sunday through the following
 * Sunday. Otherwise seven days: tomorrow through the following Sunday. **Late
 * is tomorrow**: after today.ts's LATE_HOUR (22) the list still starts
 * tomorrow; 8.2 decides whether Tonight is drawn.
 *
 * `tz` is the current region's zone (C2.1): the days and the evening are the
 * person's, wherever the rows are.
 */
export function weekAhead(now: Date, tz: string): DayPlan[] {
  const today = dayIn(now, tz)
  const todayDow = dayOfWeek(today)

  // Always start on tomorrow
  let day = addDays(today, 1)

  // Eight days only when today is Saturday (extends Sun-Sun), otherwise seven
  const loopCount = todayDow === 6 ? 8 : 7

  const result: DayPlan[] = []

  for (let i = 0; i < loopCount; i++) {
    const dayDow = dayOfWeek(day)
    const isWeekend = dayDow === 0 || dayDow === 6
    const cap =
      isWeekend && (todayDow === 4 || todayDow === 5) ? WEEKEND_AHEAD_CAP :
      isWeekend ? WEEKEND_DAY_CAP :
      WEEKNIGHT_CAP

    // `from` is weekday evening or weekend midnight
    const from = instantAt(day, isWeekend ? 0 : EVENING_HOUR, tz).toISOString()

    result.push({
      day,
      from,
      cap,
      weekend: isWeekend,
    })

    day = addDays(day, 1)
  }

  return result
}
